import ChartDataDTO, {
  IChartDataItemDTO
} from '../infractructure/dto/ChartDataDTO';

export enum ChartPointType {
  T12A = 'T12A',
  T12T = 'T12T',
  T12K = 'T12K',
  T12M = 'T12M',
  T12E = 'T12E',
  T12M4 = 'T12M_4'
}

export enum ExternalChartPointType {
  T12E_EXT = 'T12E_EXT',
  T12E_EX1 = 'T12E_EX1'
}

export type ChartPoint = ChartPointType | ExternalChartPointType;

export const AllChartPointTypes: ChartPoint[] = [
  ...Object.values(ChartPointType),
  ...Object.values(ExternalChartPointType)
];

export const ChartPointColor: Record<ChartPoint, string> = {
  [ChartPointType.T12A]: 'green',
  [ChartPointType.T12T]: 'yellow',
  [ChartPointType.T12K]: 'red50',
  [ChartPointType.T12M]: 'gray500',
  [ChartPointType.T12E]: 'lightBlue',
  [ChartPointType.T12M4]: 'lightPurple',
  [ExternalChartPointType.T12E_EXT]: 'lightOrange',
  [ExternalChartPointType.T12E_EX1]: 'green700'
};

export interface BarItem {
  type: ChartPoint;
  number: number;
  color: string;
}

export interface VerticalStackedBarsData {
  stackedBars: BarItem[];
  xAxisLabels: {
    tooltipInfo?: string;
    top: string;
    bottom: string;
  };
}

export interface IChartData {
  chartData: IChartDataItem[];
  topValueOnChart: number;
}

export interface IChartDataItem {
  timeRange: string;
  startTime: string;
  endTime: string;
  stackedBars: BarItem[];
  topValue: number;
  xAxisLabels: {
    tooltipInfo?: string;
    top: string;
    bottom: string;
  };
}

class ChartData implements IChartData {
  chartData: IChartDataItem[];
  topValueOnChart: number;

  constructor({ chartData, topValueOnChart }: IChartData) {
    this.chartData = chartData;
    this.topValueOnChart = topValueOnChart;
  }

  private static generateTimeSlots(
    rangeStart: Date,
    rangeEnd: Date,
    slotDurationMinutes: number
  ): {
    timeRange: string;
    startTime: string;
    endTime: string;
    groupKey: string;
  }[] {
    const slots: {
      timeRange: string;
      startTime: string;
      endTime: string;
      groupKey: string;
    }[] = [];

    const current = new Date(rangeStart.getTime());
    if (current.getUTCMinutes() < 45) {
      current.setUTCHours(current.getUTCHours() - 1);
    }
    current.setUTCMinutes(45, 0, 0);
    while (current < rangeEnd) {
      const next = new Date(
        current.getTime() + slotDurationMinutes * 60 * 1000
      );

      // Valores UTC utilizados para matching y llamadas al backend
      const startTimeIso = ChartData.formatUtcForApi(current);
      const endTimeIso = ChartData.formatUtcForApi(next);

      // Valores Europe/Madrid utilizados solo para presentación
      const startHourLabel = ChartData.formatSpainTime(current);
      const endHourLabel = ChartData.formatSpainTime(next);
      const timeRange = `${startHourLabel} - ${endHourLabel}`;

      const groupKey = `${startTimeIso}_${endTimeIso}`;

      slots.push({
        timeRange,
        startTime: startTimeIso,
        endTime: endTimeIso,
        groupKey
      });

      current.setTime(next.getTime());
    }

    return slots;
  }

  static generateFromDTO(
    chartDataDTO: ChartDataDTO,
    rangeStart: Date,
    rangeEnd: Date,
    slotDurationMinutes: number
  ): ChartData {
    const groupedItems = chartDataDTO.reduce(
      (
        acc: Record<
          string,
          {
            timeRange: string;
            startTime: string;
            endTime: string;
            rawValues: Record<ChartPoint, number>;
          }
        >,
        { time_range, start_time, end_time, point, value }: IChartDataItemDTO
      ) => {
        const normalizedStartTime = ChartData.formatUtcForApi(
          ChartData.parseUtcDate(start_time)
        );
        const normalizedEndTime = ChartData.formatUtcForApi(
          ChartData.parseUtcDate(end_time)
        );
        const groupKey = `${normalizedStartTime}_${normalizedEndTime}`;

        if (!acc[groupKey]) {
          acc[groupKey] = {
            timeRange: time_range,
            startTime: normalizedStartTime,
            endTime: normalizedEndTime,
            rawValues: {
              [ChartPointType.T12A]: 0,
              [ChartPointType.T12T]: 0,
              [ChartPointType.T12K]: 0,
              [ChartPointType.T12M]: 0,
              [ChartPointType.T12E]: 0,
              [ChartPointType.T12M4]: 0,
              [ExternalChartPointType.T12E_EXT]: 0,
              [ExternalChartPointType.T12E_EX1]: 0
            }
          };
        }

        acc[groupKey].rawValues[point as ChartPoint] = value;

        return acc;
      },
      {}
    );
    const slots = ChartData.generateTimeSlots(
      rangeStart,
      rangeEnd,
      slotDurationMinutes
    );
    let topValueOnChart = 0;
    const chartData = slots.map((slot) => {
      const existing = groupedItems[slot.groupKey];

      const rawValues: Record<ChartPoint, number> = existing
        ? existing.rawValues
        : {
            [ChartPointType.T12A]: 0,
            [ChartPointType.T12T]: 0,
            [ChartPointType.T12K]: 0,
            [ChartPointType.T12M]: 0,
            [ChartPointType.T12E]: 0,
            [ChartPointType.T12M4]: 0,
            [ExternalChartPointType.T12E_EXT]: 0,
            [ExternalChartPointType.T12E_EX1]: 0
          };

      const stackedBars: BarItem[] = AllChartPointTypes.map((type) => ({
        type,
        number: rawValues[type],
        color: ChartPointColor[type]
      }));

      const total = stackedBars.reduce((sum, bar) => sum + bar.number, 0);
      if (total > topValueOnChart) topValueOnChart = total;
      return {
        timeRange: slot.timeRange,
        startTime: slot.startTime,
        endTime: slot.endTime,
        topValue: total,
        stackedBars,
        xAxisLabels: {
          tooltipInfo: '',
          top: '',
          bottom: String(total)
        }
      };
    });

    return new ChartData({
      chartData,
      topValueOnChart
    });
  }

  filterByPoints(filterBy: ChartPoint[]): ChartData {
    const filteredSet = new Set(filterBy);

    const filteredChartData = this.chartData.map((item) => {
      const filteredBars = item.stackedBars.filter((bar) =>
        filteredSet.has(bar.type)
      );

      const topValue = filteredBars.reduce((sum, bar) => sum + bar.number, 0);

      return {
        ...item,
        stackedBars: filteredBars,
        topValue,
        xAxisLabels: {
          ...item.xAxisLabels,
          bottom: String(topValue)
        }
      };
    });

    const topValueOnChart = filteredChartData.reduce(
      (max, item) => Math.max(max, item.topValue),
      0
    );

    return new ChartData({
      chartData: filteredChartData,
      topValueOnChart
    });
  }

  static generateEmpty(): ChartData {
    return new ChartData({
      chartData: [],
      topValueOnChart: 0
    });
  }

  static parseUtcDate(value: string): Date {
    const normalized = value.includes('T') ? value : value.replace(' ', 'T');

    return new Date(
      normalized.endsWith('Z')
        ? normalized
        : `${normalized}${normalized.length === 16 ? ':00' : ''}Z`
    );
  }

  static formatUtcForApi(date: Date): string {
    return date.toISOString().slice(0, 19);
  }

  static formatSpainTime(date: Date): string {
    return new Intl.DateTimeFormat('es-ES', {
      timeZone: 'Europe/Madrid',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    }).format(date);
  }
}

export default ChartData;
